import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "../lib/config";

const LINKS = [
  { label: "Método Pulse", href: "#metodo" },
  { label: "Soluções", href: "#solucoes" },
  { label: "Pulse Hub", href: "#hub" },
  { label: "Academy", href: "#academy" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-ink/90 backdrop-blur-xl border-b border-gold/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <a
          href="#hero"
          onClick={go("#hero")}
          data-testid="nav-logo-link"
          className="flex items-center"
        >
          <img
            src="/pulse-logo.png"
            alt="Pulse Saúde — Marketing & Tecnologia"
            className="h-12 w-auto"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-9" data-testid="nav-desktop-links">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={go(l.href)}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-sm font-medium text-bone/70 hover:text-gold transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-diagnostico"
            onClick={go("#diagnostico")}
            className="hidden sm:inline-flex items-center gap-2 bg-gold text-ink font-display font-bold text-sm px-5 py-2.5 rounded-full hover:bg-bone transition-colors duration-300 group"
          >
            Diagnóstico Gratuito
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-bone p-2"
            aria-label="Abrir menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-b border-gold/20"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={go(l.href)}
                  data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                  className="text-lg font-display font-semibold text-bone/80 hover:text-gold transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <button
                data-testid="nav-mobile-cta-diagnostico"
                onClick={go("#diagnostico")}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-gold text-ink font-display font-bold px-6 py-3.5 rounded-full"
              >
                Diagnóstico Gratuito
                <ArrowUpRight size={18} />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
